import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaHistory, FaCalendarAlt, FaMapMarkerAlt, FaCity, FaTag } from 'react-icons/fa';
import { BASE_URL } from '../Utils/Function';
import BackgroundEffect from './BackgroundEffect';

const BookingHistory = () => {
    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchBookings = async () => {
            const storedUser = localStorage.getItem('user');
            if (!storedUser) {
                navigate('/login');
                return;
            }

            const userData = JSON.parse(storedUser);
            const userId = userData.user_id;

            try {
                const response = await axios.get(`${BASE_URL}/booking/getBookingById.php?user_id=${userId}`);
                if (response.data.status === 200) {
                    setBookings(response.data.data);
                } else {
                    setBookings([]);
                }
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    setBookings([]);
                } else {
                    setError('Unable to retrieve project history');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchBookings();
    }, [navigate]);

    const getStatusStyle = (status) => {
        // Status 1: Pending, 2: Confirmed, 3: Completed, 4: Cancelled
        switch (status) {
            case '1': return { color: '#ffc107', label: 'PENDING' };
            case '2': return { color: '#0dcaf0', label: 'CONFIRMED' };
            case '3': return { color: '#198754', label: 'COMPLETED' };
            case '4': return { color: '#dc3545', label: 'CANCELLED' };
            default: return { color: '#6c757d', label: 'UNKNOWN' };
        }
    };

    return (
        <div className="booking-history-page">
            <BackgroundEffect />
            <div className="container py-5 mt-5">
                <div className="text-center mb-5">
                    <span className="section-index">ARCHIVE</span>
                    <h2 className="display-5 fw-lighter mt-2 text-uppercase" style={{ letterSpacing: '4px', color: 'var(--secondary-color)' }}>
                        Booking History
                    </h2>
                    <div className="blueprint-line mt-3 mx-auto" style={{ width: '80px', height: '1px', background: 'var(--secondary-color)' }}></div>
                </div>

                {isLoading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-gold" role="status">
                            <span className="visually-hidden">Loading archive...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="alert-arch error mx-auto" style={{ maxWidth: '600px' }}>{error}</div>
                ) : bookings.length === 0 ? (
                    <div className="text-center py-5">
                        <FaHistory className="mb-4 opacity-25" style={{ fontSize: '4rem' }} />
                        <p className="text-white-50 letter-spacing-2">NO PROJECT RECORDS FOUND</p>
                        <button className="btn-gold px-4 py-2 mt-3" onClick={() => navigate('/#service')}>
                            BOOK YOUR FIRST SERVICE
                        </button>
                    </div>
                ) : (
                    <div className="row g-4">
                        {bookings.map((booking, index) => (
                            <div key={booking.booked_id || index} className="col-lg-6">
                                <div className="history-card">
                                    <div className="d-flex flex-column flex-md-row gap-4">
                                        <div className="history-visual">
                                            {booking.image_url ? (
                                                <img
                                                    src={booking.image_url.startsWith('http') ? booking.image_url : `${BASE_URL}/${booking.image_url}`}
                                                    alt={booking.serv_name}
                                                />
                                            ) : (
                                                <div className="fallback-visual d-flex align-items-center justify-content-center">
                                                    <FaTag style={{ fontSize: '2rem', opacity: 0.2 }} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="history-details flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <h4 className="fw-lighter m-0 text-uppercase" style={{ fontSize: '1.2rem', letterSpacing: '1px' }}>
                                                    {booking.serv_name}
                                                </h4>
                                                <span
                                                    className="status-pill"
                                                    style={{
                                                        borderColor: getStatusStyle(booking.status).color,
                                                        color: getStatusStyle(booking.status).color
                                                    }}
                                                >
                                                    {getStatusStyle(booking.status).label}
                                                </span>
                                            </div>

                                            <div className="info-grid mt-4">
                                                <div className="info-item">
                                                    <FaCalendarAlt className="me-2 text-white-50" />
                                                    <span>{new Date(booking.booked_date).toLocaleDateString()}</span>
                                                </div>
                                                <div className="info-item">
                                                    <FaCity className="me-2 text-white-50" />
                                                    <span>{booking.town}</span>
                                                </div>
                                                <div className="info-item w-100">
                                                    <FaMapMarkerAlt className="me-2 text-white-50" />
                                                    <span className="small opacity-75">{booking.address}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="blueprint-decor"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .booking-history-page {
                    min-height: 100vh;
                    background: #050505;
                    color: #fff;
                    padding-bottom: 80px;
                }

                .history-card {
                    background: linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 100%);
                    border: 1px solid rgba(255,255,255,0.08);
                    padding: 25px;
                    border-radius: 4px;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;
                }

                .history-card:hover {
                    border-color: var(--secondary-color);
                    background: rgba(197, 160, 89, 0.05);
                }

                .history-visual {
                    width: 120px;
                    height: 120px;
                    flex-shrink: 0;
                    border: 1px solid rgba(255,255,255,0.1);
                    overflow: hidden;
                    border-radius: 2px;
                }

                .history-visual img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    padding: 8px;
                }

                .fallback-visual {
                    width: 100%;
                    height: 100%;
                    background: rgba(255,255,255,0.02);
                }

                .status-pill {
                    font-size: 0.55rem;
                    padding: 3px 10px;
                    border: 1px solid;
                    letter-spacing: 2px;
                    font-weight: 500;
                    border-radius: 20px;
                }

                .info-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 15px;
                }

                .info-item {
                    display: flex;
                    align-items: center;
                    font-size: 0.8rem;
                    letter-spacing: 1px;
                }

                .blueprint-decor {
                    position: absolute;
                    bottom: -10px;
                    right: -10px;
                    width: 50px;
                    height: 50px;
                    border-bottom: 1px solid var(--secondary-color);
                    border-right: 1px solid var(--secondary-color);
                    opacity: 0.1;
                    pointer-events: none;
                }

                .alert-arch {
                    padding: 20px;
                    font-size: 0.75rem;
                    letter-spacing: 1px;
                    border-left: 3px solid;
                    text-align: center;
                }
                .alert-arch.error { border-color: #dc3545; color: #dc3545; background: rgba(220, 53, 69, 0.05); }

                @media (max-width: 576px) {
                    .booking-history-page { padding-top: 20px; }
                    .display-5 { font-size: 1.8rem; }
                    .history-visual { 
                        width: 100%; 
                        height: clamp(150px, 40vw, 220px); 
                    }
                    .history-card { 
                        padding: 20px; 
                        text-align: center;
                    }
                    .history-card .d-flex {
                        align-items: center;
                    }
                    .info-grid {
                        justify-content: center;
                        gap: 10px;
                    }
                    .info-item {
                        font-size: 0.75rem;
                    }
                    .status-pill {
                        display: inline-block;
                        margin: 10px auto;
                        background: rgba(197, 160, 89, 0.1);
                    }
                    .history-details .d-flex.justify-content-between {
                        flex-direction: column;
                        gap: 10px;
                        align-items: center !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default BookingHistory;
